import React from "react";
import { Grid } from "@mui/material";
import EventCardComponent from "../../../../components/core-components/eventsCard";
import SnackbarComponent from "../../../../components/core-components/snackbar";
import { SnackbarCloseReason } from "@mui/material/Snackbar";
import TitleSectionComponent from "../../../../components/core-components/titleSection";
import { PostsService } from "../../../../network/PostsService";
import { useQuery } from "@tanstack/react-query";

// Define interface that matches EventCardProps
interface EventData {
  _id: string;       // Changed from id: number to _id: string
  content: string;   // Added as required by EventCardComponent
  title: string;
  createdAt: Date;   // Added as required by EventCardComponent
  image: string;
  link?: string;     // Made optional
  description?: string | string[]; // Made optional
  date: string;      // Keep for sorting
  [key: string]: any; // For any additional properties
}

// Helper function to convert Turkish month names to numerical values
function parseMonthTurkish(monthName: string): number | undefined {
  const months: Record<string, number> = {
    "Ocak": 0,
    "Şubat": 1,
    "Mart": 2,
    "Nisan": 3,
    "Mayıs": 4,
    "Haziran": 5,
    "Temmuz": 6,
    "Ağustos": 7,
    "Eylül": 8,
    "Ekim": 9,
    "Kasım": 10,
    "Aralık": 11
  };
  return months[monthName];
}

// Parse Turkish date string in format "DD Ayismi YYYY"
function parseTurkishDate(dateStr: string): Date {
  if (!dateStr) return new Date(0);
  
  const parts = dateStr.split(" ");
  if (parts.length !== 3) {
    console.warn("Invalid date format:", dateStr);
    return new Date(0); // Return earliest date if invalid
  }
  
  const day = parseInt(parts[0], 10);
  const monthName = parts[1];
  const month = parseMonthTurkish(monthName);
  const year = parseInt(parts[2], 10);
  
  if (isNaN(day) || month === undefined || isNaN(year)) {
    console.warn("Invalid date components:", { day, month, year });
    return new Date(0);
  }
  
  return new Date(year, month, day);
}

// Tarihi görüntüleme fonksiyonu
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Date formatting error:', error);
    return dateString;
  }
};

function Events(): JSX.Element {
  const [open, setOpen] = React.useState<boolean>(false);
  const handleClick = (): void => {
    setOpen(true);
  };

  const postsService = new PostsService();

  const { data, isLoading } = useQuery({
    queryKey: ["posts"],
    queryFn: async () => {
      const res = await postsService.getAll();
      return res?.data as EventData[];
    },
  });

  // Sort events by date (most recent first)
  const sortedEvents = React.useMemo(() => {
    if (!data) return [];
    
    return [...data].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  }, [data]);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ): void => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  return (
    <section
      id="events"
      style={{
        backgroundColor: "var(--bg-color)",
        height: "100%",
        paddingBottom: "100px",
      }}
    >
      <div className="container">
        <TitleSectionComponent titleText="Etkinlikler" />

        <Grid container spacing={4}>
          {isLoading ? (
            <Grid item xs={12}>
              <p>Etkinlikler yükleniyor...</p>
            </Grid>
          ) : sortedEvents.length > 0 ? (
            sortedEvents.map((event, index) => (
              <Grid item xs={12} sm={6} md={4} key={event._id || `event-${index}`}>
                <EventCardComponent 
                  event={{
                    ...event,
                    date: formatDate(event.date)
                  }} 
                  handleClick={handleClick} 
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <p>Etkinlik bulunamadı.</p>
            </Grid>
          )}
        </Grid>
        
        <SnackbarComponent
          open={open}
          handleClose={handleClose}
          alertText="Etkinlik linki başarıyla kopyalandı!"
        />
      </div>
    </section>
  );
}

export default Events;